/**
 * Parses the numeric values inside each object of the array to match the specified locale.
 * Non-numeric values (strings like "-") are preserved as-is.
 *
 * @param data - Array of objects with dynamic keys containing numeric or string values.
 * @param locale - BCP 47 locale string (e.g. 'de-DE', 'en-GB', 'fr-FR').
 * @returns A new array of objects with numeric values formatted according to the locale.
 */

type TParsedData = Record<string, string>;

export const formatDataFromLocale = (
  data: Record<string, unknown>[] | null | undefined,
  locale: string,
): TParsedData[] => {
  if (!data) return [];
  const formatter = new Intl.NumberFormat(locale);

  return data.map((row) => {
    const formattedRow: TParsedData = {};

    Object.entries(row).forEach(([key, value]) => {
      if (typeof value === 'number') {
        formattedRow[key] = formatter.format(value);
      } else {
        formattedRow[key] = String(value ?? '');
      }
    });

    return formattedRow;
  });
};

/**
 * Formats a single numeric value according to the specified locale.
 * Returns the value as-is (stringified) if it is not a finite number.
 *
 * @param value - The value to format. Only finite numbers are locale-formatted.
 * @param locale - BCP 47 locale string (e.g. 'de-DE', 'en-GB', 'fr-FR').
 * @returns The locale-formatted number string, or a plain string fallback ('-' for nullish values).
 */
export const formatNumberFromLocale = (value: unknown, locale: string): string => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return new Intl.NumberFormat(locale).format(value);
  }
  return String(value ?? '-');
};
